<script setup>
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Card, Button, Badge, Drawer } from '@drama/ui'
import { Clock, Copy, Check, Code, ChevronRight, RefreshCw, AlertTriangle } from '@lucide/vue'
import { listPromptPacks, getPromptPack } from '../../api/story-film-prompt-packs.js'

const { t } = useI18n()
const packs = ref([])
const loading = ref(false)
const error = ref('')
const nextCursor = ref(null)

const activeDrawer = ref(false)
const detail = ref(null)
const detailLoading = ref(false)
const detailError = ref('')
const copiedKey = ref('')

async function copyText(text, key) {
  if (!text) return
  try {
    await navigator.clipboard.writeText(String(text))
    copiedKey.value = key
    setTimeout(() => {
      if (copiedKey.value === key) copiedKey.value = ''
    }, 2000)
  } catch {
    // clipboard fallback
  }
}

function statusLabel(status) {
  return t(`visualGameAdmin.storyFilmPromptReview.status.${status}`)
}

function statusTone(status) {
  if (status === 'approved' || status === 'ready') return 'success'
  if (status === 'rejected' || status === 'failed') return 'danger'
  if (status === 'review_ready') return 'info'
  return 'neutral'
}

function safeLogText(value) {
  return typeof value === 'string' ? value : JSON.stringify(value, null, 2)
}

function safeLogJson(record) {
  return JSON.stringify(record, null, 2)
}

const detailShots = computed(() => detail.value?.request?.shots ?? [])
const detailSource = computed(() => detail.value?.request?.source ?? {})
const detailBudget = computed(() => detail.value?.request?.budget ?? {})
const detailProvider = computed(() => detail.value?.request?.provider ?? {})
const detailPlan = computed(() => detail.value?.request?.plan ?? {})

async function loadPacks(reset = true) {
  if (loading.value) return
  loading.value = true
  error.value = ''
  try {
    const query = { limit: 20 }
    if (!reset && nextCursor.value) query.before_id = nextCursor.value
    const result = await listPromptPacks(query)
    packs.value = reset ? result.items : [...packs.value, ...result.items]
    nextCursor.value = result.next_cursor ?? null
  } catch (cause) {
    error.value = cause?.message || '加载历史快照失败'
  } finally {
    loading.value = false
  }
}

async function viewPackDetail(id) {
  activeDrawer.value = true
  detailLoading.value = true
  detailError.value = ''
  detail.value = null
  try {
    detail.value = await getPromptPack(id)
  } catch (cause) {
    detailError.value = cause?.message || '加载快照详情失败'
  } finally {
    detailLoading.value = false
  }
}

onMounted(() => {
  loadPacks(true)
})
</script>

<template>
  <div class="prompt-pack-history">
    <div class="pack-history-head">
      <div class="pack-history-title">
        <Clock :size="15" />
        <h4>历史请求快照 (Prompt Pack Snapshots)</h4>
        <span class="pack-count-tag">{{ packs.length }} 条已加载</span>
      </div>
      <Button size="sm" variant="ghost" :disabled="loading" @click="loadPacks(true)">
        <RefreshCw :size="13" :class="{ 'is-spinning': loading }" />
        <span>刷新</span>
      </Button>
    </div>

    <p v-if="error" class="history-error">
      <AlertTriangle :size="14" />
      <span>{{ error }}</span>
    </p>

    <!-- 历史快照表格 -->
    <div class="pack-table-wrap">
      <table class="pack-table">
        <thead>
          <tr>
            <th>#</th>
            <th>{{ t('visualGameAdmin.storyFilmPromptReview.columns.episode') }}</th>
            <th>{{ t('visualGameAdmin.storyFilmPromptReview.columns.version') }}</th>
            <th>{{ t('visualGameAdmin.storyFilmPromptReview.columns.status') }}</th>
            <th>{{ t('visualGameAdmin.storyFilmPromptReview.columns.shots') }}</th>
            <th>{{ t('visualGameAdmin.storyFilmPromptReview.columns.duration') }}</th>
            <th>{{ t('visualGameAdmin.storyFilmPromptReview.columns.digest') }}</th>
            <th>{{ t('visualGameAdmin.storyFilmPromptReview.columns.createdAt') }}</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="pack in packs" :key="pack.id" :class="{ 'is-selected': detail && detail.id === pack.id }">
            <td class="mono-text">#{{ pack.id }}</td>
            <td class="mono-text">{{ pack.episode_id }}</td>
            <td><span class="version-tag">v{{ pack.version_no }}</span></td>
            <td><Badge :variant="statusTone(pack.status)" size="sm">{{ statusLabel(pack.status) }}</Badge></td>
            <td>{{ pack.shots_count }} 镜</td>
            <td>{{ Math.round(pack.total_duration_ms / 1000) }}s</td>
            <td class="mono-text digest-cell" :title="pack.pack_digest">{{ pack.pack_digest ? pack.pack_digest.slice(0, 16) + '...' : '—' }}</td>
            <td class="date-cell">{{ pack.created_at }}</td>
            <td>
              <Button size="sm" variant="ghost" class="view-btn" @click="viewPackDetail(pack.id)">
                <span>查看快照</span>
                <ChevronRight :size="13" />
              </Button>
            </td>
          </tr>
          <tr v-if="!loading && packs.length === 0">
            <td colspan="9" class="empty-cell">{{ t('visualGameAdmin.storyFilmPromptReview.empty') }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="nextCursor" class="pack-more-row">
      <Button size="sm" variant="secondary" :disabled="loading" @click="loadPacks(false)">
        {{ t('visualGameAdmin.storyFilmPromptReview.actions.more') }}
      </Button>
    </div>

    <!-- 快照详情抽屉 (Drawer) -->
    <Drawer :open="activeDrawer" title="Prompt Pack 离线快照详情" @close="activeDrawer = false">
      <div v-if="detailLoading" class="drawer-loading">
        <RefreshCw class="is-spinning" :size="24" />
        <span>加载快照中…</span>
      </div>
      <div v-else-if="detailError" class="history-error">
        <AlertTriangle :size="16" />
        <span>{{ detailError }}</span>
      </div>
      <div v-else-if="detail" class="pack-detail-drawer">
        <!-- 头部摘要 -->
        <header class="detail-drawer-head">
          <div class="head-info">
            <span class="head-sub">Prompt Pack #{{ detail.id }}</span>
            <h3 class="head-title">版本 v{{ detail.version_no }}</h3>
          </div>
          <Badge :variant="statusTone(detail.status)" size="md">{{ statusLabel(detail.status) }}</Badge>
        </header>

        <!-- 元数据网格 -->
        <div class="facts-grid">
          <div class="fact-item"><span class="fact-label">所属剧目</span><b>{{ detail.film_id }}</b></div>
          <div class="fact-item"><span class="fact-label">季 / 集</span><b>第 {{ detailSource.season_no ?? '—' }} 季 · 第 {{ detailSource.episode_no ?? '—' }} 集</b></div>
          <div class="fact-item"><span class="fact-label">Run / 分支</span><span class="mono-text">{{ detailSource.run_id }} / {{ detailSource.branch_id }}</span></div>
          <div class="fact-item"><span class="fact-label">来源跨度</span><b>{{ detailSource.from_seq }} – {{ detailSource.to_seq }}</b></div>
          <div class="fact-item"><span class="fact-label">模型供应商</span><b>{{ detailProvider.provider ?? '—' }} ({{ detailProvider.model ?? '—' }})</b></div>
          <div class="fact-item"><span class="fact-label">画面尺寸</span><b>{{ detailProvider.width }} × {{ detailProvider.height }}</b></div>
          <div class="fact-item"><span class="fact-label">预算状态</span><b>{{ detailBudget.status ?? '—' }} (预估 {{ detailBudget.predicted_points ?? '—' }} 积分)</b></div>
          <div class="fact-item"><span class="fact-label">镜头总览</span><b>{{ detailPlan.shots ?? detailShots.length }} 镜 / {{ Math.round((detailPlan.total_duration_ms ?? 0) / 1000) }}s</b></div>
        </div>

        <!-- 镜头列表明细 -->
        <div class="shots-section">
          <div class="shots-section-head">
            <h4>镜头分包规划 ({{ detailShots.length }} 镜)</h4>
          </div>

          <div class="shot-cards-flow">
            <div v-for="shot in detailShots" :key="shot.index" class="shot-detail-card">
              <header class="shot-card-header">
                <div class="shot-card-title">
                  <strong>#{{ shot.index }}</strong>
                  <span class="shot-frames-tag">{{ shot.h3_frames }}f · {{ (shot.h3_duration_ms / 1000).toFixed(1) }}s</span>
                  <span class="shot-seq-tag">Seq {{ shot.from_seq }}–{{ shot.to_seq }}</span>
                </div>
                <div class="shot-card-badges">
                  <span v-if="shot.predicted_cost?.points" class="cost-tag">{{ shot.predicted_cost.points }} 积分</span>
                  <span class="sendable-badge" :class="shot.request_sendable ? 'is-ok' : 'is-blocked'">
                    {{ shot.request_sendable ? '可发' : '阻断' }}
                  </span>
                </div>
              </header>

              <!-- Prompt 详情 -->
              <div v-if="shot.prompt?.full" class="shot-prompt-block">
                <div class="prompt-block-head">
                  <span class="prompt-label">提示词 Prompt ({{ shot.chars ?? shot.prompt.full.length }} 字符)</span>
                  <Button size="sm" variant="ghost" class="mini-copy" @click="copyText(shot.prompt.full, `p_${shot.index}`)">
                    <Check v-if="copiedKey === `p_${shot.index}`" :size="12" class="is-ok-icon" />
                    <Copy v-else :size="12" />
                  </Button>
                </div>
                <div class="prompt-text-box">{{ safeLogText(shot.prompt.full) }}</div>
              </div>

              <!-- 负向提示词 -->
              <div v-if="shot.prompt?.negative_prompt" class="shot-negative-block">
                <span class="negative-label">负向提示词:</span>
                <span class="negative-text">{{ safeLogText(shot.prompt.negative_prompt) }}</span>
              </div>

              <!-- 原始请求 JSON 折叠 -->
              <details class="request-accordion">
                <summary>原始 request JSON</summary>
                <pre class="request-pre">{{ safeLogJson(shot.request) }}</pre>
              </details>
            </div>
          </div>
        </div>

        <!-- 历史事件流 -->
        <div v-if="detail.events?.length" class="events-section">
          <h4>事件留痕 ({{ detail.events.length }})</h4>
          <div class="events-list">
            <div v-for="event in detail.events" :key="event.id" class="event-row">
              <span class="event-time">{{ event.created_at }}</span>
              <span class="event-action">{{ event.action }}</span>
              <span class="event-user mono-text">{{ event.actor_user_id }}</span>
              <span v-if="event.notes" class="event-notes">{{ safeLogText(event.notes) }}</span>
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  </div>
</template>

<style scoped>
.prompt-pack-history {
  display: flex;
  flex-direction: column;
  gap: var(--drama-space-sm);
  width: 100%;
}

.pack-history-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--drama-space-xs) 0;
}

.pack-history-title {
  display: flex;
  align-items: center;
  gap: var(--drama-space-xs);
}

.pack-history-title h4 {
  margin: 0;
  font-size: var(--drama-font-size-sm);
  font-weight: 600;
}

.pack-count-tag {
  font-size: 11px;
  color: var(--drama-color-text-muted);
  background: var(--drama-color-surface-alt);
  padding: 1px 6px;
  border-radius: var(--drama-radius-full);
}

.history-error {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--drama-color-danger);
  font-size: var(--drama-font-size-xs);
  margin: 0;
}

.pack-table-wrap {
  width: 100%;
  overflow-x: auto;
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface);
}

.pack-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11.5px;
  text-align: left;
}

.pack-table th {
  padding: 6px 10px;
  background: var(--drama-color-surface-alt);
  color: var(--drama-color-text-muted);
  font-weight: 600;
  border-bottom: 1px solid var(--drama-color-border);
  white-space: nowrap;
}

.pack-table td {
  padding: 6px 10px;
  border-bottom: 1px solid var(--drama-color-border);
  vertical-align: middle;
}

.pack-table tr:last-child td {
  border-bottom: none;
}

.pack-table tr:hover td {
  background: var(--drama-color-surface-alt);
}

.pack-table tr.is-selected td {
  background: color-mix(in srgb, var(--drama-color-primary) 8%, var(--drama-color-surface));
}

.mono-text {
  font-family: var(--drama-font-family-mono);
}

.version-tag {
  font-weight: 600;
  color: var(--drama-color-primary);
}

.digest-cell {
  max-width: 140px;
  color: var(--drama-color-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.date-cell {
  color: var(--drama-color-text-muted);
  white-space: nowrap;
}

.view-btn {
  font-size: 11px;
  padding: 2px 6px;
}

.empty-cell {
  text-align: center;
  padding: 20px;
  color: var(--drama-color-text-muted);
}

.pack-more-row {
  display: flex;
  justify-content: center;
  padding: var(--drama-space-xs) 0;
}

/* 抽屉内容 */
.drawer-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 40px;
  color: var(--drama-color-text-muted);
}

.pack-detail-drawer {
  display: flex;
  flex-direction: column;
  gap: var(--drama-space-md);
  padding: var(--drama-space-xs) 0;
}

.detail-drawer-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: var(--drama-space-sm);
  border-bottom: 1px solid var(--drama-color-border);
}

.head-sub {
  font-size: 11px;
  color: var(--drama-color-text-muted);
}

.head-title {
  margin: 2px 0 0;
  font-size: var(--drama-font-size-md);
  font-weight: 600;
}

.facts-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 12px;
  padding: 10px;
  background: var(--drama-color-surface-alt);
  border-radius: var(--drama-radius-md);
  font-size: 11px;
}

.fact-item {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.fact-label {
  font-size: 10px;
  color: var(--drama-color-text-muted);
}

/* 镜头明细卡片 */
.shots-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.shots-section-head h4 {
  margin: 0;
  font-size: 12px;
  font-weight: 600;
}

.shot-cards-flow {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 50vh;
  overflow-y: auto;
  padding-right: 4px;
}

.shot-detail-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  background: var(--drama-color-surface);
  font-size: 11px;
}

.shot-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.shot-card-title {
  display: flex;
  align-items: center;
  gap: 6px;
}

.shot-frames-tag, .shot-seq-tag {
  font-size: 10px;
  color: var(--drama-color-text-muted);
  background: var(--drama-color-surface-alt);
  padding: 1px 5px;
  border-radius: 3px;
}

.shot-card-badges {
  display: flex;
  align-items: center;
  gap: 4px;
}

.cost-tag {
  font-size: 10px;
  color: var(--drama-color-text-muted);
}

.sendable-badge {
  font-size: 10px;
  padding: 1px 5px;
  border-radius: 999px;
}

.sendable-badge.is-ok {
  background: color-mix(in srgb, var(--drama-color-success) 12%, transparent);
  color: var(--drama-color-success);
}

.sendable-badge.is-blocked {
  background: color-mix(in srgb, var(--drama-color-danger) 12%, transparent);
  color: var(--drama-color-danger);
}

.shot-prompt-block {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.prompt-block-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.prompt-label {
  font-size: 10px;
  font-weight: 600;
  color: var(--drama-color-text-muted);
}

.mini-copy {
  padding: 0 4px;
  height: 20px;
}

.prompt-text-box {
  padding: 6px 8px;
  background: var(--drama-color-surface-alt);
  border-radius: var(--drama-radius-sm);
  font-family: var(--drama-font-family-mono);
  font-size: 10.5px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.shot-negative-block {
  font-size: 10px;
  color: var(--drama-color-warning);
  padding: 4px 6px;
  background: color-mix(in srgb, var(--drama-color-warning) 8%, transparent);
  border-radius: 3px;
}

.request-accordion summary {
  font-size: 10px;
  color: var(--drama-color-text-muted);
  cursor: pointer;
  margin-top: 2px;
}

.request-pre {
  margin: 4px 0 0;
  font-family: var(--drama-font-family-mono);
  font-size: 10px;
  background: var(--drama-color-surface-alt);
  padding: 6px;
  border-radius: 3px;
  max-height: 120px;
  overflow-y: auto;
}

.events-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 11px;
}

.events-section h4 {
  margin: 0;
  font-size: 11.5px;
  font-weight: 600;
}

.events-list {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.event-row {
  display: flex;
  gap: 6px;
  align-items: baseline;
  font-size: 10px;
  color: var(--drama-color-text-muted);
}

.event-action {
  color: var(--drama-color-text);
  font-weight: 600;
}

.is-spinning {
  animation: spin 1s linear infinite;
}

.is-ok-icon {
  color: var(--drama-color-success);
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
